import React from 'react';
import { useLazyGetCityByGeoRadiusQuery } from 'store/api/activitiesApi';
import { GeolocationStatus, useGeolocation } from 'core/utils/useGeolocation';
import { mockGeoRadiusResult } from 'mock/nearMe/mock-geo-radius-result';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { NearBySuggestion } from 'core/type/PlaceSuggestion';
import { CityInfoByGeoRadiusResponse } from 'store/api/api.types';
import { isQueryLoading } from 'store/api/apiUtils';
import { measureCityByGeoRadiusApiResponse } from 'store/api/measurements/cityByGeoRadiusApiMeasurement';
import { useSelector } from 'react-redux';
import { selectPageTypeId } from 'store/slice/navigation';
import { selectConfigFromBootstrap } from 'store/slice/bootstrap';

const getCityInfoByGeoRadius = (response: CityInfoByGeoRadiusResponse, pageTypeId: number, config: BootstrapConfig): NearBySuggestion | null => {
    if (config.isMockData) {
        return mockGeoRadiusResult;
    }
    if (!isQueryLoading(response)) {
        measureCityByGeoRadiusApiResponse(response, pageTypeId, config);
    }
    if (response.isSuccess) {
        return response?.data || null;
    }
    return null;
};

export const useCityInfoByLatLong = () => {
    const [location, requestLocation, status] = useGeolocation();
    const config = useSelector(selectConfigFromBootstrap);
    const [triggerGetCityByGeoRadius, cityByRadiusResponse] = useLazyGetCityByGeoRadiusQuery();
    const [shouldFetchLocation, setShouldFetchLocation] = React.useState(false);
    const pageTypeId = useSelector(selectPageTypeId);

    const isLocationGranted = status === GeolocationStatus.GRANTED;

    const cityInfoData = React.useMemo(
        () => getCityInfoByGeoRadius(cityByRadiusResponse as CityInfoByGeoRadiusResponse, pageTypeId, config),
        [cityByRadiusResponse, config, pageTypeId],
    );

    React.useEffect(() => {
        if (location && shouldFetchLocation && isLocationGranted) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            triggerGetCityByGeoRadius({ latitude: location.latitude, longitude: location.longitude });
            setShouldFetchLocation(false);
        }
    }, [shouldFetchLocation, isLocationGranted, location, triggerGetCityByGeoRadius]);

    const fetchUserLocation = React.useCallback(() => {
        if (!location) {
            requestLocation();
        }
        setShouldFetchLocation(true); // set flag to fetch city by radius on user demand
    }, [location, requestLocation]);

    return { cityByRadiusResponse: cityInfoData, fetchUserLocation, status };
};
